/**
 * Fixed-timestep logic loop, driven by requestAnimationFrame but decoupled
 * from it: logic always advances in whole 1/20s ticks regardless of display
 * refresh rate, so a 144Hz monitor and a 60Hz one produce identical eggs.
 */

export const TICKS_PER_SECOND = 20;
export const TICK_MS = 1000 / TICKS_PER_SECOND;
/** Seconds of game time per tick, handed to tick callbacks. */
export const TICK_SECONDS = 1 / TICKS_PER_SECOND;

/**
 * Most ticks one frame may run (10 seconds' worth). rAF stops firing in a
 * hidden tab, so returning to one can leave a huge accumulated delta; without
 * a clamp the loop would freeze catching up. Time away is credited by the
 * offline-progress path in M4 from `lastSaved`, not by replaying ticks here.
 */
export const MAX_CATCHUP_TICKS = 200;

/**
 * Frames are drawn at most about 60 times a second. rAF fires at the display's
 * rate, so a 120 or 144 Hz screen drew the yard two or three times as often
 * for no visible gain; the logic has its own 20-tick clock either way. The
 * floor sits a little under 1/60 s so a 60 Hz display, whose frames jitter
 * around 16.7 ms, still draws every one.
 */
export const MIN_RENDER_MS = 13;

export interface LoopCallbacks {
  /** Advance the simulation by `seconds` (always TICK_SECONDS). */
  tick(seconds: number): void;
  /** Draw. `alpha` is progress into the next tick, for interpolation in M5. */
  render(alpha: number): void;
}

export interface Loop {
  start(): void;
  stop(): void;
  /**
   * Forget accumulated time (after a hidden tab comes back, once the gap has
   * been credited as offline progress) so no catch-up ticks run for it.
   */
  resync(): void;
}

export interface TickPlan {
  ticks: number;
  /** Milliseconds left over, carried into the next frame. */
  remainder: number;
  /** True when the clamp discarded backlog, so callers can note the skip. */
  clamped: boolean;
}

/** Pure: how many ticks to run for an accumulated millisecond budget. */
export function planTicks(accumulatedMs: number): TickPlan {
  if (accumulatedMs < TICK_MS) {
    return { ticks: 0, remainder: Math.max(0, accumulatedMs), clamped: false };
  }
  const ticks = Math.floor(accumulatedMs / TICK_MS);
  if (ticks > MAX_CATCHUP_TICKS) {
    return { ticks: MAX_CATCHUP_TICKS, remainder: 0, clamped: true };
  }
  return { ticks, remainder: accumulatedMs - ticks * TICK_MS, clamped: false };
}

export function createLoop(callbacks: LoopCallbacks): Loop {
  let rafId = 0;
  let lastMs = 0;
  let accumulator = 0;
  let lastRenderMs = -Infinity;
  let running = false;

  const frame = (nowMs: number): void => {
    if (!running) return;
    rafId = requestAnimationFrame(frame);

    accumulator += nowMs - lastMs;
    lastMs = nowMs;

    const plan = planTicks(accumulator);
    for (let i = 0; i < plan.ticks; i++) {
      callbacks.tick(TICK_SECONDS);
    }
    accumulator = plan.remainder;

    if (nowMs - lastRenderMs < MIN_RENDER_MS) return;
    lastRenderMs = nowMs;
    callbacks.render(accumulator / TICK_MS);
  };

  return {
    start(): void {
      if (running) return;
      running = true;
      lastMs = performance.now();
      accumulator = 0;
      rafId = requestAnimationFrame(frame);
    },
    stop(): void {
      running = false;
      cancelAnimationFrame(rafId);
    },
    resync(): void {
      lastMs = performance.now();
      accumulator = 0;
    },
  };
}
